// frontend/src/pages/CategoryPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaUser, FaChevronLeft } from 'react-icons/fa';
import API_CONFIG from '../config/api';
import useCategories from './hooks/useCategories';
import './CategoryPage.css';
import HeaderWrapper from '../components/common/HeaderWrapper';
import { encodeId } from '../utils/idMasker'

const CategoryPage = () => {
    // State management
    const [categoryData, setCategoryData] = useState(null);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const imageServer = API_CONFIG.BASE_URL;
    const navigate = useNavigate();
    const location = useLocation();

    // Handle back navigation
    const handleGoBack = () => {
        if (location.key !== "default") {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    // Get categoryId and categoryName from URL path parameter
    const { categoryId, categoryName } = useParams();

    // Convert kebab-case categoryName to Title Case for display
    const displayCategoryName = useMemo(() => {
        if (!categoryName) return null;
        return categoryName
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }, [categoryName]);

    // Use the categories hook
    const {
        getCategoryNameById,
        getCategoryById,
        isLoading: categoriesLoading
    } = useCategories();

    const defaultCoverImage = useMemo(() => '/default-cover.jpg', []);


    const renderPageCard = useCallback((page) => {
        const contentPreview = page.description;
        // Determine the correct ID to use for the post details page
        // Based on the API response, we should use the page_id if available
        const displayCover = page.cover_image_url ? imageServer + page.cover_image_url : defaultCoverImage;
        // Check all possible ID fields
        const pageId = page.page_id || page.id;
        const maskedId = encodeId(pageId); // e.g., 42 -> "XF03sR4"
        return (
            <div key={pageId} className="page-card">
                <Link to={`/${page.slug}/post/${maskedId}`} className="card-link">
                    <div className="card-content">
                        <h3 className="card-title">{page.title || 'Untitled Page'}</h3>
                        <div className="card-secondary-image">
                            <img
                                src={displayCover}
                                alt="Content preview"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = defaultCoverImage;
                                }}
                            />
                        </div>
                        {contentPreview && (
                            <div className="content-preview">
                                <p>{contentPreview}</p>
                            </div>
                        )}

                    </div>
                </Link>
            </div>
        );
    }, [defaultCoverImage, imageServer]);

    const renderCreatorCard = useCallback((creator) => {
        return (
            <div key={creator.id} className="page-card">
                <Link to={`/${creator.username || creator.id}`} className="card-link">
                    <div className="access-tag creator">
                        <FaUser /> CREATOR
                    </div>
                    <div className="card-content">
                        <h3 className="card-title">{creator.username || 'Unknown User'}</h3>
                        <div className="card-secondary-image">
                            <img
                                src={creator.avatar_url || '/default-cover.jpg'}
                                alt="Creator avatar"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                        <div className="category-label-container">
                            <span className="category-name">{creator.user_type || 'Creator'}</span>
                        </div>
                        <div className="content-preview">
                            <p>Testing the description</p>
                        </div>
                    </div>
                </Link>
            </div>
        );
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchCategoryData = async () => {
            if (!categoryId) {
                setError('No category ID provided');
                setLoading(false);
                return;
            }

            // Wait for categories to load before fetching category data
            if (categoriesLoading) {
                return;
            }

            try {
                setLoading(true);
                setError('');

                // Get category info from the hook
                const categoryInfo = getCategoryById(categoryId);
                if (categoryInfo) {
                    setCategoryData({
                        name: categoryInfo.name,
                        description: categoryInfo.description || `Explore content in the ${categoryInfo.name} category`
                    });
                }

                // Call the backend API endpoint as shown in Swagger docs
                // GET /api/v1/pages/category/{category_id}
                const response = await axios.get(`${API_CONFIG.BASE_URL}/api/v1/pages/category/${categoryId}`);
                console.log('API Response:', response.data);

                if (isMounted) {
                    // If we get array data directly
                    if (Array.isArray(response.data)) {
                        // Enhance post data with additional properties for better rendering and navigation
                        const enhancedPosts = response.data.map(post => ({
                            ...post,
                            category: post.category || categoryInfo?.name || '',
                            // Ensure id is available
                            id: post.id || post.page_id || post._id,
                            // Ensure we have a fallback creator
                            creator: post.creator || { username: 'CC8' }
                        }));
                        setPages(enhancedPosts);

                        // If we haven't set category data yet, try to get it from the first item
                        if (!categoryInfo && response.data.length > 0 && response.data[0].category_id) {
                            const categoryName = getCategoryNameById(response.data[0].category_id);
                            setCategoryData({
                                name: categoryName,
                                description: `Explore content in the ${categoryName} category`
                            });
                        }
                    }
                    // If we get an object with data inside
                    else if (response.data) {
                        // Store category metadata if available
                        if (response.data.name || response.data.description) {
                            setCategoryData(response.data);
                        }
                        
                        // Get posts from appropriate property
                        if (Array.isArray(response.data.posts)) {
                            // Enhance post data
                            const enhancedPosts = response.data.posts.map(post => ({
                                ...post,
                                category: post.category || categoryInfo?.name || '',
                                // Ensure id is available
                                id: post.id || post.page_id || post._id,
                                // Ensure we have a fallback creator
                                creator: post.creator || { username: 'CC8' }
                            }));
                            setPages(enhancedPosts);
                        } else if (Array.isArray(response.data.pages)) {
                            // Enhance page data
                            const enhancedPages = response.data.pages.map(page => ({
                                ...page,
                                category: page.category || categoryInfo?.name || '',
                                // Ensure id is available
                                id: page.id || page.page_id || page._id,
                                // Ensure we have a fallback creator
                                creator: page.creator || { username: 'CC8' }
                            }));
                            setPages(enhancedPages);
                        } else {
                            // If no posts array, the whole response might be the posts
                            setPages([]);
                        }
                    } else {
                        setError('No data received from server');
                    }
                }
            } catch (err) {
                console.error('Error fetching category data:', err);
                if (isMounted) {
                    setPages([]);
                    setCategoryData(null);
                    setError(err.response?.data?.message || 'Failed to load category data');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchCategoryData();

        return () => {
            isMounted = false;
        };
    }, [categoryId, categoriesLoading, getCategoryById, getCategoryNameById]);

    // Show loading if either categories or page data is loading
    if (loading || categoriesLoading) {
        return (
            <>
                <HeaderWrapper />
                <div className="category-page-container">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading category content...</p>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <HeaderWrapper />
                <div className="category-page-container">
                    <div className="error-container">
                        <h2>Something went wrong</h2>
                        <p>{error}</p>
                        <Link to="/" className="btn-primary">Return to Home</Link>
                    </div>
                </div>
            </>
        );
    }

    // Get category name from categoryData if available, otherwise use the URL-based name
    const finalCategoryName = categoryData?.name || displayCategoryName || getCategoryNameById(categoryId);
    // const categoryDescription = categoryData?.description || `Discover content in the ${categoryName} category`;

    return (
        <>
            <HeaderWrapper />
            {/* Breadcrumb Navigation */}
            <div className="breadcrumb-bar">
                <button onClick={handleGoBack} className="breadcrumb-back-btn">
                    <FaChevronLeft /> Back to Previous Page
                </button>
            </div>
            <div className="category-page-container">
                <div className="category-header">
                    <h1>{finalCategoryName}</h1>
                </div>
                {pages.length > 0 ? (
                    <div className="page-grid">
                        {pages[0] && pages[0].email && !pages[0].slug
                            ? pages.map(creator => renderCreatorCard(creator))
                        : pages.map(page => renderPageCard(page))}
                    </div>
                ) : (
                    <div className="no-pages-found">
                        <h2>No Pages Found</h2>
                        <p>There are no pages available in the "{finalCategoryName}" category yet.</p>
                        <Link to="/" className="btn-primary">Explore other categories</Link>
                    </div>
                )}
            </div>
        </>
    );
};

export default CategoryPage;